import { useState, useEffect, useRef } from 'react';
import { useLatest } from './useLatest';

// A failed listener is re-opened after 30 s, then 1, 2, 4 … minutes, at most every 10.
const RETRY_FIRST_MS = 30 * 1000;
const RETRY_MAX_MS = 10 * 60 * 1000;
export const retryDelay = failures => Math.min(RETRY_MAX_MS, RETRY_FIRST_MS * 2 ** Math.max(0, failures - 1));

const same = (a, b) => a === b;

/**
 * useFeed — keep one data/ subscription open while the screen is mounted, and re-open it
 * after an error. Every live screen of Daily Ops reads Firestore through this.
 *
 * Firestore closes a failed listener for good (over the daily quota, rules said no,
 * network trouble on the first load). The screens used to stay frozen on old data until
 * someone reloaded the page — the kitchen tablet, left open, never caught up after the
 * quota reset. Now a failed feed is tried again after RETRY_FIRST_MS, backing off to
 * RETRY_MAX_MS, and at once when the screen comes back on.
 *
 *   key        what it subscribes to; a new key subscribes afresh (e.g. another list id)
 *   subscribe  (onData(data, meta?), onError(err)) → unsubscribe — read when (re)opening
 *   initial    `data` until the first snapshot
 *   enabled    false = no listener at all (`data` stays `initial`)
 *   select     (data, meta) → what to keep; an update that selects an equal value
 *              (`===`) doesn't re-render (e.g. the nav badge keeps only a count)
 * @returns {{ data, meta, error, loading }} — `data` keeps the last good value through an
 *   error; `error` is the Firestore error (null once data flows again).
 */
export function useFeed(key, subscribe, { initial, enabled = true, select } = {}) {
  const [state, setState] = useState(() => ({ key, data: initial, meta: undefined, error: null, loading: enabled }));
  const [attempt, setAttempt] = useState(0);
  const sub = useLatest({ subscribe, select, initial });
  const failures = useRef({ key, n: 0 });   // back-off count, per key

  useEffect(() => {
    if (!enabled) return undefined;
    if (failures.current.key !== key) failures.current = { key, n: 0 };
    let alive = true;
    let timer = null;
    const unsubscribe = sub.current.subscribe(
      (data, meta) => {
        if (!alive) return;
        failures.current.n = 0;
        const pick = sub.current.select;
        const next = pick ? pick(data, meta) : data;
        const nextMeta = pick ? undefined : meta;
        setState(s => (s.key === key && !s.loading && !s.error && same(s.data, next) && same(s.meta, nextMeta)
          ? s : { key, data: next, meta: nextMeta, error: null, loading: false }));
      },
      err => {
        if (!alive) return;
        failures.current.n += 1;
        // Keep showing the last good data of THIS key (another key's never shows).
        setState(s => ({
          key, error: err || new Error('Could not load'), loading: false,
          data: s.key === key ? s.data : sub.current.initial, meta: s.key === key ? s.meta : undefined,
        }));
        timer = setTimeout(() => setAttempt(n => n + 1), retryDelay(failures.current.n));
      },
    );
    return () => {
      alive = false;
      clearTimeout(timer);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [key, attempt, enabled, sub]);

  // The screen comes back on while the feed is down → try again now, not at the next back-off.
  const down = !!state.error;
  useEffect(() => {
    if (!down || typeof document === 'undefined') return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') setAttempt(n => n + 1); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [down]);

  if (!enabled) return { data: initial, meta: undefined, error: null, loading: false };
  // A new key: nothing of the old one's shows while the new one loads.
  if (state.key !== key) return { data: initial, meta: undefined, error: null, loading: true };
  return state;
}
